import { getScheduledBroadcasts, getWorkspaceWorkflowItems } from "@storyboard/web-client";
import { transport } from "@/config";
import { createLogger } from "@/logger";
import { bulkUpsertWsAudioEncodings } from "@/models/repositories/AudioEncoding";
import { bulkUpsertBroadcastAction } from "@/models/repositories/BroadcastAction";
import { bulkUpsertBroadcastRecipients } from "@/models/repositories/BroadcastRecipient";
import { bulkUpsertDisplayArtifact } from "@/models/repositories/DisplayArtifact";
import { bulkUpsertFile } from "@/models/repositories/File";
import { bulkUpsertLink } from "@/models/repositories/Link";
import { bulkUpsertScheduleTrigger } from "@/models/repositories/ScheduleTrigger";
import { bulkUpsertWsTranscriptions } from "@/models/repositories/Transcription";
import { bulkUpsertWorkflowItem } from "@/models/repositories/WorkflowItem";

const log = createLogger("bootstrap");

/**
 * Replaces the local workflow tables with the server's current set: workflow items with their content, scheduled
 * broadcasts with their triggers, actions and recipients.
 *
 * The workflow routes take no delta cursor, so this is a whole-set fetch — the Workflows page polls it and the AppSync
 * sync layer calls it whenever a workflow row changes.
 */
export async function loadWorkspaceWorkflowItems({
  workspaceId,
  myAccountId,
}: {
  workspaceId: string;
  myAccountId: string;
}): Promise<void> {
  if (!myAccountId || !workspaceId) return;
  const items = await getWorkspaceWorkflowItems(transport, { workspaceId });
  log.debug("Workspace workflow items response", { items });
  if (items?.workflowItems?.length) await bulkUpsertWorkflowItem(items.workflowItems);
  if (items?.audioEncodings?.length) await bulkUpsertWsAudioEncodings(items.audioEncodings);
  if (items?.files?.length) await bulkUpsertFile(items.files);
  if (items?.links?.length) await bulkUpsertLink(items.links);
  if (items?.transcriptions?.length) await bulkUpsertWsTranscriptions(items.transcriptions);
  if (items?.displayArtifacts?.length) await bulkUpsertDisplayArtifact(items.displayArtifacts);

  const broadcasts = await getScheduledBroadcasts(transport, { workspaceId });
  if (broadcasts?.scheduleTriggers?.length) await bulkUpsertScheduleTrigger(broadcasts.scheduleTriggers);
  if (broadcasts?.broadcastActions?.length) await bulkUpsertBroadcastAction(broadcasts.broadcastActions);
  if (broadcasts?.broadcastRecipients?.length) await bulkUpsertBroadcastRecipients(broadcasts.broadcastRecipients);
}
